/**
 * Telling a tap on a star apart from a drag across the sky.
 *
 * Its own file, like `routing.ts` and `sky-layout.ts`, for the same reason: it
 * is pure arithmetic that decides something a child notices immediately, and a
 * pure function can be tested without a browser. The bug that produced it could
 * not be caught any other way — it only appeared with a real pointing device,
 * and it appeared on one kind of device and not the other.
 */

/**
 * How far a pointer may travel between press and release and still count as a
 * tap, in SCREEN pixels.
 *
 * Screen pixels, not sky units: an earlier version scaled this by the zoom, so
 * the threshold moved as a child pinched — far out, one pixel of movement could
 * exceed it; far in, a real drag might not.
 *
 * Two numbers, because a finger and a mouse are not the same instrument. A
 * mouse is held still and reports every pixel it crosses; a finger lands on
 * glass, rolls, and leaves. Native toolkits make the same split for the same
 * reason. One number would mean fixing the browser by breaking the tablet: 7px
 * is generous for a mouse and mean for a seven-year-old.
 */
export const TAP_SLOP_PX = { fine: 7, coarse: 16 };

/** A finger gets the larger allowance. A pen is a steady hand, so it does not. */
export function slopFor(pointerType: string): number {
  return pointerType === "touch" ? TAP_SLOP_PX.coarse : TAP_SLOP_PX.fine;
}

/**
 * Did this pointer travel far enough to be a drag rather than a tap?
 *
 * Measured from where it was PRESSED, not from the previous event. That
 * distinction is the whole bug: comparing the step since the last event meant a
 * mouse click with three pixels of hand-wobble arrived as one 3px step and was
 * called a drag, so the click was swallowed and the star never opened. A finger
 * never triggered it, because iOS holds back the small movements inside its own
 * tap slop — which is why the iPad was fine and only the browser looked broken.
 *
 * The same mistake ran the other way too: a slow drag made of one-pixel steps
 * never tripped the old test at all, so panning gently across the sky opened
 * whichever star the finger happened to stop on.
 */
export function isDrag(
  from: { x: number; y: number },
  to: { x: number; y: number },
  pointerType: string,
): boolean {
  return Math.hypot(to.x - from.x, to.y - from.y) > slopFor(pointerType);
}

/**
 * What a single pointer move means: are we dragging, and may the sky move?
 *
 * Two answers rather than one, because treating them as the same question is
 * what made a star unclickable in a browser. Recognising the drag decided
 * whether to swallow the click — but the sky panned on *every* move regardless,
 * so a click carrying two pixels of wobble slid the star out from under the
 * cursor before the button came up. Press and release then landed on different
 * elements, the browser dispatched `click` at their common ancestor, and the
 * star's own handler never ran. It still took focus on mousedown, so it drew a
 * focus ring and did nothing.
 *
 * Holding the view still until the gesture is a drag means a tap cannot move
 * its own target.
 *
 * `pan` is false on the move that CROSSES the threshold as well, so the sky
 * does not jerk by the slop distance when a drag begins.
 */
export function gestureStep(
  press: { x: number; y: number },
  to: { x: number; y: number },
  pointerType: string,
  wasDragging: boolean,
): { dragging: boolean; pan: boolean } {
  const dragging = wasDragging || isDrag(press, to, pointerType);
  return { dragging, pan: dragging && wasDragging };
}
